import { useState, useEffect } from "react";
import axios from "axios";
import React from "react";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const API = "https://erp-xe9u.onrender.com/api";

const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#1F2937",
    borderColor: "#374151",
    color: "#D1D5DB",
    "&:hover": { borderColor: "#F97316" },
    boxShadow: "none",
    minHeight: "36px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#D1D5DB",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#D1D5DB",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6B7280",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#374151" : "#1F2937",
    color: state.isSelected ? "#F97316" : "#D1D5DB",
    "&:hover": { backgroundColor: "#374151" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1F2937",
    borderRadius: "0.375rem",
    border: "1px solid #374151",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
  }),
};

const STATUS_CONFIG = {
  qualified: {
    color: "#10B981",
    border: "border-emerald-500/30",
    text: "text-emerald-400",
    bg: "bg-emerald-500",
    label: "Qualified",
  },
  unqualified: {
    color: "#F59E0B",
    border: "border-amber-500/30",
    text: "text-amber-400",
    bg: "bg-amber-500",
    label: "Unqualified",
  },
  disqualified: {
    color: "#EF4444",
    border: "border-red-500/30",
    text: "text-red-400",
    bg: "bg-red-500",
    label: "Disqualified",
  },
};

const COLORS = [
  "#F97316",
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#8B5CF6",
  "#F59E0B",
  "#EC4899",
  "#14B8A6",
];

export default function LeadsAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState("year");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const getTimeRangeOptions = () => {
    const base = [
      { value: "current", label: "All Time" },
      { value: "year", label: `${new Date().getFullYear()}` },
      { value: "month", label: "This Month" },
    ];
    if (analytics && analytics.allAvailableYears) {
      const currentYear = new Date().getFullYear();
      analytics.allAvailableYears
        .filter((y) => y < currentYear)
        .sort((a, b) => b - a)
        .forEach((year) =>
          base.push({ value: String(year), label: String(year) }),
        );
    }
    return base;
  };

  const fetchAnalytics = async (overrideTimeRange) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");
      const tr = overrideTimeRange || timeRange;
      const response = await axios.get(
        `${API}/leads/analytics?timeRange=${tr}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.data.success)
        throw new Error(response.data.message || "Failed to fetch analytics");
      setAnalytics(response.data.analytics);
    } catch (err) {
      console.error("Analytics fetch error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch analytics.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const getStatusPieData = () => {
    if (!analytics || !analytics.statusBreakdown) return [];
    const sb = analytics.statusBreakdown;
    return [
      {
        name: "Qualified",
        value: sb.qualified,
        color: STATUS_CONFIG.qualified.color,
      },
      {
        name: "Unqualified",
        value: sb.unqualified,
        color: STATUS_CONFIG.unqualified.color,
      },
      {
        name: "Disqualified",
        value: sb.disqualified,
        color: STATUS_CONFIG.disqualified.color,
      },
    ].filter((d) => d.value > 0);
  };

  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 px-3 py-2 border border-gray-700 rounded-md shadow-lg">
          {label && (
            <p className="text-xs font-medium text-gray-400 mb-1">{label}</p>
          )}
          {payload.map((entry, i) => (
            <p key={i} className="text-sm text-gray-200">
              <span
                className="inline-block w-2 h-2 rounded-full mr-1.5"
                style={{ backgroundColor: entry.color || entry.fill }}
              />
              {entry.name}: <span className="font-semibold">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const rateColor = (rate) => {
    if (rate >= 50) return "text-emerald-400";
    if (rate >= 25) return "text-amber-400";
    return "text-red-400";
  };

  const sb = analytics ? analytics.statusBreakdown : null;

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <Select
          options={getTimeRangeOptions()}
          value={getTimeRangeOptions().find((o) => o.value === timeRange)}
          onChange={(sel) => setTimeRange(sel ? sel.value : "year")}
          placeholder="Period"
          className="w-40"
          styles={customStyles}
          isSearchable={false}
        />
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <svg
            className="animate-spin h-7 w-7 text-orange-500"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
            />
          </svg>
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      {analytics && !loading && (
        <>
          {/* ===== KPI Cards ===== */}
          {sb && (
            <div className="grid grid-cols-4 gap-3 mb-5">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700/60">
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-medium mb-1">
                  Total Leads
                </p>
                <p className="text-3xl font-bold text-gray-100 tabular-nums">
                  {sb.total}
                </p>
              </div>
              {["qualified", "unqualified", "disqualified"].map((key) => {
                const cfg = STATUS_CONFIG[key];
                const count = sb[key] || 0;
                const pct = sb[key + "Pct"] || 0;
                return (
                  <div
                    key={key}
                    className={`bg-gray-800 rounded-lg p-4 border ${cfg.border}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">
                        {cfg.label}
                      </p>
                      <span className={`text-xs font-semibold ${cfg.text}`}>
                        {pct}%
                      </span>
                    </div>
                    <p
                      className={`text-3xl font-bold ${cfg.text} tabular-nums`}
                    >
                      {count}
                    </p>
                    <div className="mt-2 h-1 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${cfg.bg} rounded-full transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ===== Row 2: Status Donut + Disqualification Reasons ===== */}
          <div className="grid grid-cols-5 gap-3 mb-5">
            {/* Donut */}
            <div className="col-span-2 bg-gray-800 rounded-lg border border-gray-700/60 p-4">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                Status Distribution
              </h4>
              {getStatusPieData().length > 0 ? (
                <div className="flex items-center">
                  <div className="w-[55%]">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie
                          data={getStatusPieData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={75}
                          paddingAngle={3}
                          dataKey="value"
                          stroke="none"
                        >
                          {getStatusPieData().map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-[45%] space-y-2.5 pl-2">
                    {getStatusPieData().map((entry) => (
                      <div key={entry.name} className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-xs text-gray-400 flex-1">
                          {entry.name}
                        </span>
                        <span className="text-sm font-semibold text-gray-200 tabular-nums">
                          {entry.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">
                  No data
                </p>
              )}
            </div>

            {/* Disqualification reasons */}
            <div className="col-span-3 bg-gray-800 rounded-lg border border-gray-700/60 p-4">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                Disqualification Reasons
              </h4>
              {analytics.disqualificationReasons &&
              analytics.disqualificationReasons.length > 0 ? (
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="pb-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider w-20">
                        Count
                      </th>
                      <th className="pb-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider w-20">
                        Share
                      </th>
                      <th className="pb-2 w-32"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {analytics.disqualificationReasons.map((reason) => (
                      <tr
                        key={reason.category}
                        className="hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="py-2 text-sm text-gray-300">
                          {reason.category}
                        </td>
                        <td className="py-2 text-sm text-gray-200 text-right font-medium tabular-nums">
                          {reason.count}
                        </td>
                        <td className="py-2 text-sm text-right">
                          <span className="text-red-400 font-medium tabular-nums">
                            {reason.percentage}%
                          </span>
                        </td>
                        <td className="py-2 pr-1">
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-red-500/70 rounded-full transition-all duration-500"
                              style={{ width: `${reason.percentage}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-600">
                      <td className="pt-2 text-sm font-semibold text-gray-300">
                        Total
                      </td>
                      <td className="pt-2 text-sm font-semibold text-gray-200 text-right tabular-nums">
                        {analytics.disqualificationReasons.reduce(
                          (s, r) => s + r.count,
                          0,
                        )}
                      </td>
                      <td className="pt-2 text-sm font-semibold text-gray-400 text-right">
                        100%
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-gray-500 text-sm text-center py-8">
                  No disqualified leads
                </p>
              )}
            </div>
          </div>

          {/* ===== Row 3: Source Table + Monthly Trend ===== */}
          <div className="grid grid-cols-2 gap-3">
            {/* Source breakdown */}
            {analytics.sourceBreakdown &&
            analytics.sourceBreakdown.length > 0 ? (
              <div className="bg-gray-800 rounded-lg border border-gray-700/60 p-4">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                  Leads by Source
                </h4>
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="pb-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="pb-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider w-14">
                        Count
                      </th>
                      <th className="pb-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider w-20">
                        Share
                      </th>
                      <th className="pb-2 w-32"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {analytics.sourceBreakdown.map((source) => (
                      <tr
                        key={source.source}
                        className="hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="py-2 text-sm text-gray-300">
                          {source.source}
                        </td>
                        <td className="py-2 text-sm text-gray-200 text-center font-medium tabular-nums">
                          {source.count}
                        </td>
                        <td className="py-2 text-sm text-right">
                          <span className="text-orange-400 font-medium tabular-nums">
                            {source.percentage}%
                          </span>
                        </td>
                        <td className="py-2 pr-1">
                          <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-orange-500/70 rounded-full transition-all duration-500"
                              style={{ width: `${source.percentage}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-600">
                      <td className="pt-2 text-sm font-semibold text-gray-300">
                        Total
                      </td>
                      <td className="pt-2 text-sm font-semibold text-gray-200 text-center tabular-nums">
                        {analytics.sourceBreakdown.reduce(
                          (s, r) => s + r.count,
                          0,
                        )}
                      </td>
                      <td className="pt-2 text-sm font-semibold text-gray-400 text-right">
                        100%
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg border border-gray-700/60 p-4">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                  Leads by Source
                </h4>
                <p className="text-gray-500 text-sm text-center py-8">
                  No source data
                </p>
              </div>
            )}

            {/* Monthly trend */}
            {analytics.monthlyTrend && analytics.monthlyTrend.length > 0 ? (
              <div className="bg-gray-800 rounded-lg border border-gray-700/60 p-4">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                  Monthly Trend
                </h4>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={analytics.monthlyTrend} barCategoryGap="20%">
                    <XAxis
                      dataKey="month"
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={{ stroke: "#374151" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      width={30}
                    />
                    <Tooltip
                      content={<CustomChartTooltip />}
                      cursor={{ fill: "rgba(255,255,255,0.03)" }}
                    />
                    <Bar
                      dataKey="qualified"
                      name="Qualified"
                      fill="#10B981"
                      stackId="a"
                    />
                    <Bar
                      dataKey="unqualified"
                      name="Unqualified"
                      fill="#F59E0B"
                      stackId="a"
                    />
                    <Bar
                      dataKey="disqualified"
                      name="Disqualified"
                      fill="#EF4444"
                      stackId="a"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 flex items-center justify-center gap-4 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-emerald-500" />{" "}
                    Qualified
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-amber-500" />{" "}
                    Unqualified
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-red-500" />{" "}
                    Disqualified
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg border border-gray-700/60 p-4">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                  Monthly Trend
                </h4>
                <p className="text-gray-500 text-sm text-center py-8">
                  No monthly data
                </p>
              </div>
            )}
          </div>
        </>
      )}

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
        onConfirm={alert.onConfirm}
      />
    </div>
  );
}
